import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api/api';

const MFAVerification = () => {
  const [mfaCode, setMfaCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get MFA data from location state or localStorage
    const mfaData = location.state?.mfaData || JSON.parse(localStorage.getItem('mfaData') || '{}');
    
    if (mfaData.email && mfaData.mfa_token) {
      setEmail(mfaData.email);
      setMfaToken(mfaData.mfa_token);
    } else {
      // Redirect back to login if no MFA data
      toast.error('MFA verification required. Please login first.');
      navigate('/login');
    }
  }, [location, navigate]);

  const handleMFAVerification = async (e) => {
    e.preventDefault();
    
    if (!mfaCode.trim()) {
      toast.error('Please enter your MFA code');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/verify-mfa', {
        email: email,
        mfa_token: mfaToken,
        mfa_code: mfaCode
      });

      if (response.data.access_token) {
        console.log('=== MFA VERIFICATION SUCCESS ===');
        console.log('Response data:', response.data);
        console.log('Redirect URL:', response.data.redirect_url);
        
        // Store the access token
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('Token stored in localStorage');
        console.log('Token preview:', response.data.access_token.substring(0, 20) + '...');
        console.log('Full token length:', response.data.access_token.length);
        
        // Clear MFA data
        localStorage.removeItem('mfaData');
        
        // Update API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        console.log('API headers updated with token');
        
        toast.success('MFA verification successful!');
        
        // Redirect based on user role
        const redirectUrl = response.data.redirect_url || '/dashboard';
        console.log('Navigating to:', redirectUrl);
        
        // Force page reload to ensure AuthContext picks up the new token
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 100);
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Invalid MFA code. Please try again.');
      } else {
        toast.error('MFA verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendMFA = () => {
    toast.info('Please use your authenticator app to generate a new code.');
  };

  const handleBackToLogin = () => {
    localStorage.removeItem('mfaData');
    navigate('/login');
  };

  if (!email || !mfaToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Loading...
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #207CE5 0%, #008B8B 100%)'
    }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white p-3 rounded-full shadow-lg">
              <img 
                src="/Vital-logo.png" 
                alt="VITAL AUDIO Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Sonometrik</h1>
        </div>

        {/* MFA Form */}
        <div className="glass-effect rounded-2xl p-8 shadow-xl">
          <div className="text-center mb-6">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Multi-Factor Authentication</h2>
            <p className="text-white text-sm mt-1 font-medium">
              {email}
            </p>
          </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleMFAVerification}>
          <div>
            <label htmlFor="mfa-code" className="block text-sm font-medium text-white mb-2">
              MFA Code
            </label>
            <input
              id="mfa-code"
              name="mfa-code"
              type="text"
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-va-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent text-center text-lg tracking-widest"
              placeholder="Enter MFA code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              maxLength={20}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>

          <div className="flex flex-col space-y-3">
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {isLoading ? 'Verifying...' : 'Verify MFA Code →'}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white/80 bg-transparent hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </form>
        </div>

        <div className="mt-6 text-center">
          <div className="text-sm text-white/80">
            <p>Having trouble?</p>
            <p className="mt-1">
              Check your authenticator app for the MFA code and try again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
